import React, { useMemo, useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
// Params are provided from Rails via ReactOnRails props
import SprintKanbanBoard from '../../components/Sprints/SprintKanbanBoard.jsx';
import Tabs from '../../../../ui/canon/Tabs.jsx';
import { CheckCircleIcon, ClockIcon, ChatBubbleLeftRightIcon, DocumentTextIcon, EyeIcon, ArrowLeftIcon, PauseIcon, CheckIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import CoachesCornerComments from '../../components/Comments/CoachesCornerComments.jsx';
import TaskDetailModal from '../../components/AdvisorMap/TaskDetailModal.jsx';
import EnrollmentFilesTab from '../../components/coachLed/EnrollmentFilesTab.jsx';

const CURRENT_USER_QUERY = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      name
      allowed_name
      company {
        id
        features
      }
    }
  }
`;

const ENROLLMENT_QUERY = gql`
  query SprintEnrollment($id: ID!) {
    sprintEnrollment(id: $id) {
      id
      status
      started_on
      target_end_on
      progress_pct
      user {
        id
        name
        allowed_name
        user_name
      }
      sprint {
        id
        name
        startOn
        endOn
        status
        source_type
        comment_count
        unread_comment_count
        items {
          id
          title
          description
          status
          priority
          dueAt
          domain
          cepaCategory
          coach_review_status
          comment_count
          unread_comment_count
          documents {
            id
            name
            download_url
            created_at
          }
          taskTemplate {
            id
            key
            title
            description
          }
        }
      }
      sprintProgram {
        id
        title
        summary
        benefits
        coachProfile {
          id
          user {
            id
          }
          name
          headline
          avatar_url
          verified
          rating
          review_count
        }
      }
      allFiles {
        id
        name
        download_url
        created_at
        file_size
        source_type
        can_delete
        uploaded_by {
          name
        }
        sprint_program {
          id
          title
        }
        sprint_enrollment {
          id
        }
      }
    }
  }
`;

const UPDATE_ITEM = gql`
  mutation UpdateActionPlanItem(
    $id: ID!
    $status: ActionPlanItemStatusEnum
    $priority: ActionPlanItemPriorityEnum
    $dueAt: ISO8601DateTime
    $assigneeUserId: ID
    $pipelineCardId: ID
    $title: String
    $description: String
    $coach_review_status: String
  ) {
    update_action_plan_item(
      id: $id
      status: $status
      priority: $priority
      dueAt: $dueAt
      assigneeUserId: $assigneeUserId
      pipelineCardId: $pipelineCardId
      title: $title
      description: $description
      coach_review_status: $coach_review_status
    ) {
      id
      status
      priority
      dueAt
      coach_review_status
    }
  }
`;

const REVIEW_ITEM = gql`
  mutation ReviewActionPlanItem(
    $id: ID!
    $review_status: String!
    $coach_notes: String
  ) {
    review_action_plan_item(
      id: $id
      review_status: $review_status
      coach_notes: $coach_notes
    ) {
      id
      status
      coach_review_status
    }
  }
`;

export default function EnrollmentWorkspaceContainer(props) {
  const enrollmentId = (props && (props.enrollment_id || props.enrollmentId)) || null;
  const { data, loading, error, refetch } = useQuery(ENROLLMENT_QUERY, {
    variables: { id: enrollmentId },
    skip: !enrollmentId,
  });
  const { data: currentUserData } = useQuery(CURRENT_USER_QUERY);
  const [updateItem] = useMutation(UPDATE_ITEM);
  const [reviewItem] = useMutation(REVIEW_ITEM);
  const [activeTab, setActiveTab] = useState('tasks');
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  const enrollment = data?.sprintEnrollment;
  const sprint = enrollment?.sprint;
  const tasks = sprint?.items || [];
  const progressPct = enrollment?.progress_pct || 0;
  const program = enrollment?.sprintProgram;
  const coach = program?.coachProfile;
  const currentUser = currentUserData?.getCurrentUser;
  const sprintCommentsEnabled = !!currentUser?.company?.features?.includes('sprint_comments');
  // Check if coach-led: either source_type is SprintProgram OR enrollment has sprintProgram
  const isCoachLed = sprint?.source_type === 'SprintProgram' || !!program;

  // Detect coach view from URL query params
  const isCoachView = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('viewAsCoach') === 'true';
  }, []);

  // Determine if current user is actually the coach
  const isCurrentUserCoach = useMemo(() => {
    if (!currentUser || !coach?.user?.id) return false;
    return String(currentUser.id) === String(coach.user.id);
  }, [currentUser, coach]);

  // Only allow coach view if user is actually the coach
  const effectiveIsCoachView = isCoachView && isCurrentUserCoach;

  // Get participant name
  const participantName = enrollment?.user?.name || enrollment?.user?.allowed_name || enrollment?.user?.user_name || 'Participant';

  const handleItemUpdate = async (itemId, updates) => {
    try {
      const vars = { id: itemId };
      if (updates.status) vars.status = String(updates.status).toUpperCase();
      if (updates.priority) vars.priority = String(updates.priority).toUpperCase();
      if (updates.dueAt) vars.dueAt = updates.dueAt;
      // Only include coach_review_status if it's explicitly set (not null/undefined)
      // Don't send null - let the backend handle clearing it
      if (updates.coach_review_status !== undefined && updates.coach_review_status !== null) {
        vars.coach_review_status = String(updates.coach_review_status).toLowerCase();
      }
      
      await updateItem({
        variables: vars,
      });
      
      // Refetch to get updated data
      await refetch();
    } catch (err) {
      console.error('Error updating item:', err);
      alert(`Failed to update task: ${err.message || 'Unknown error'}`);
    }
  };

  const handleItemClick = (item) => {
    setSelectedTask(item);
    setIsTaskModalOpen(true);
  };

  const handleTaskModalClose = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
  };

  const handleTaskReview = async (taskId, reviewStatus, coachNotes) => {
    try {
      await reviewItem({
        variables: {
          id: taskId,
          review_status: reviewStatus,
          coach_notes: coachNotes || null,
        },
      });
      await refetch();
      handleTaskModalClose();
    } catch (err) {
      console.error('Error reviewing task:', err);
      alert(`Failed to review task: ${err.message || 'Unknown error'}`);
    }
  };

  const handleTaskSave = async (taskId, updates) => {
    // Only allow saves if not in coach view (coaches can't edit tasks)
    if (effectiveIsCoachView) {
      alert('Coaches cannot edit tasks. Use the review buttons to approve or reject.');
      return;
    }
    await handleItemUpdate(taskId, updates);
  };

  const handleTaskMarkCompleted = async (taskId) => {
    // Only allow marking completed if not in coach view
    if (effectiveIsCoachView) {
      alert('Coaches cannot mark tasks as completed. Use the review buttons to approve or reject.');
      return;
    }
    await handleItemUpdate(taskId, { status: 'completed' });
  };

  const handleTaskDelete = async (taskId) => {
    // Only allow deletion if not in coach view
    if (effectiveIsCoachView) {
      alert('Coaches cannot delete tasks.');
      return;
    }
    // TODO: Implement delete mutation if needed
    alert('Delete functionality coming soon');
  };

  const daysRemaining = useMemo(() => {
    if (!enrollment?.target_end_on) return '—';
    const endDate = new Date(enrollment.target_end_on);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    return diffDays;
  }, [enrollment?.target_end_on]);

  // Calculate task counts
  const completedTasks = useMemo(() => {
    if (!isCoachLed) {
      return tasks.filter(t => String(t.status).toLowerCase() === 'completed').length;
    }
    // For coach-led sprints, only count effectively completed (approved or no review required)
    return tasks.filter(t => {
      const status = String(t.status).toLowerCase();
      if (status !== 'completed') return false;
      // If no coach_review_status, it's completed (no review required)
      if (!t.coach_review_status) return true;
      // Only count if approved
      return String(t.coach_review_status).toLowerCase() === 'approved';
    }).length;
  }, [tasks, isCoachLed]);

  const inProgressTasks = useMemo(() => {
    return tasks.filter(t => String(t.status).toLowerCase() === 'in_progress').length;
  }, [tasks]);

  const pendingReviewTasks = useMemo(() => {
    if (!isCoachLed) return 0;
    return tasks.filter(t => {
      const status = String(t.status).toLowerCase();
      const reviewStatus = t.coach_review_status ? String(t.coach_review_status).toLowerCase() : null;
      return status === 'completed' && reviewStatus === 'pending';
    }).length;
  }, [tasks, isCoachLed]);

  const totalTasks = tasks.length;
  
  // Calculate progress: include completed, in progress, and pending review tasks
  const progressTasks = completedTasks + inProgressTasks + pendingReviewTasks;
  const calculatedProgressPct = totalTasks > 0 ? Math.round((progressTasks / totalTasks) * 100) : 0;
  
  // Use sprint name or program title for the workspace title
  // Coach view shows participant name, participant view shows sprint/program name
  const workspaceTitle = effectiveIsCoachView 
    ? `${participantName}'s Workspace`
    : (sprint?.name || program?.title || 'My Sprint Workspace');
  
  // Debug logging
  React.useEffect(() => {
    if (tasks.length > 0) {
      console.log('Task status breakdown:', {
        total: totalTasks,
        completed: completedTasks,
        inProgress: inProgressTasks,
        pendingReview: pendingReviewTasks,
        progressTasks: progressTasks,
        progressPct: calculatedProgressPct,
        tasks: tasks.map(t => ({ id: t.id, status: t.status, coach_review_status: t.coach_review_status }))
      });
    }
  }, [tasks, completedTasks, inProgressTasks, pendingReviewTasks, progressTasks, calculatedProgressPct, totalTasks]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="rounded-2xl border border-border bg-card p-6">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !enrollment) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="rounded-2xl border border-border bg-card p-6">
            {error ? `Error: ${error.message}` : 'Enrollment not found'}
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'tasks', label: 'Tasks' },
    ...(sprintCommentsEnabled ? [{ id: 'messages', label: 'Messages' }] : []),
    { id: 'files', label: 'Files' },
  ];

  // If the messages tab gets gated off after a tab change, fall back to tasks
  const safeActiveTab = safeActiveTab === 'messages' && !sprintCommentsEnabled ? 'tasks' : activeTab;

  return (
    <div className="min-h-screen bg-succession-bg-primary">
      {/* Breadcrumb (participant view only — coaches use the "Back to Coach Portal" link in the banner below) */}
      {!effectiveIsCoachView && (
        <div className="border-b border-succession-border bg-succession-bg-primary">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <a
              href="/sprints"
              className="inline-flex items-center gap-2 text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>Back to Sprints</span>
            </a>
          </div>
        </div>
      )}

      {/* Coach View Banner */}
      {effectiveIsCoachView && (
        <div className="bg-purple-50 dark:bg-purple-950 border-b border-purple-200 dark:border-purple-800">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <EyeIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <span className="font-semibold text-purple-900 dark:text-purple-100">
                  Viewing as Coach
                </span>
                <span className="text-purple-700 dark:text-purple-300">
                  {participantName}
                </span>
              </div>
              <a
                href="/coach/portal"
                className="flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:underline"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Coach Portal
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-succession-border bg-succession-bg-primary">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between gap-6 mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center rounded-full border border-succession-border bg-succession-bg-secondary px-3 py-1 text-xs font-medium text-succession-text-secondary">
                  {program?.title || 'Sprint Program'}
                </span>
                <span className="inline-flex items-center rounded-full bg-succession-text-primary px-3 py-1 text-xs font-medium text-succession-bg-primary capitalize">
                  {enrollment.status || 'Active'}
                </span>
              </div>
              <h1 className="text-2xl font-bold mb-2 text-succession-text-primary">{workspaceTitle}</h1>
              {coach && (
                <div className="flex items-start gap-3">
                  {coach.avatar_url && (
                    <img
                      src={coach.avatar_url}
                      alt={coach.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-succession-text-primary">{coach.name}</h4>
                      {coach.verified && (
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    {coach.headline && (
                      <p className="text-sm text-succession-text-secondary">{coach.headline}</p>
                    )}
                    {coach.rating && (
                      <div className="flex items-center gap-1 mt-1">
                        <svg className="w-4 h-4 text-yellow-500 fill-current" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                        <span className="text-sm font-medium text-succession-text-primary">{coach.rating.toFixed(1)}</span>
                        {coach.review_count && (
                          <span className="text-sm text-succession-text-secondary">({coach.review_count})</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {effectiveIsCoachView && (
                <div className="flex items-center gap-2 mt-4">
                  {sprintCommentsEnabled && (
                    <button
                      onClick={() => setActiveTab('messages')}
                      className="inline-flex items-center px-3 py-1.5 rounded-md border border-succession-border bg-succession-bg-primary text-succession-text-primary text-sm hover:bg-succession-hover-bg transition-colors"
                    >
                      <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                      Send Message
                    </button>
                  )}
                  <button
                    onClick={() => {
                      // TODO: Implement pause enrollment mutation
                      alert('Pause enrollment coming soon');
                    }}
                    className="inline-flex items-center px-3 py-1.5 rounded-md border border-succession-border bg-succession-bg-primary text-succession-text-primary text-sm hover:bg-succession-hover-bg transition-colors"
                  >
                    <PauseIcon className="w-4 h-4 mr-2" />
                    Pause Enrollment
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Implement mark complete mutation
                      alert('Mark complete coming soon');
                    }}
                    className="inline-flex items-center px-3 py-1.5 rounded-md border border-succession-border bg-succession-bg-primary text-succession-text-primary text-sm hover:bg-succession-hover-bg transition-colors"
                  >
                    <CheckIcon className="w-4 h-4 mr-2" />
                    Mark Complete
                  </button>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm text-succession-text-secondary mb-1">Days Remaining</div>
              <div className="text-3xl font-bold text-succession-text-primary">{daysRemaining}</div>
            </div>
          </div>

          {/* Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-succession-text-primary">Progress</span>
              <span className="text-succession-text-secondary">
                {progressTasks} of {totalTasks} tasks completed
              </span>
            </div>
            <div className="h-2 rounded-full bg-succession-bg-secondary overflow-hidden">
              <div
                className="h-2 bg-succession-primary rounded-full transition-all"
                style={{ width: `${Math.min(100, Math.max(0, calculatedProgressPct))}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs tabs={tabs} value={safeActiveTab} onChange={setActiveTab} showDescription={false} />

        <div className="mt-6">
          {safeActiveTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                      <CheckCircleSolidIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="font-semibold text-succession-text-primary">Completed</h3>
                  </div>
                  <div className="text-3xl font-bold text-succession-text-primary">{completedTasks}</div>
                  <p className="text-sm text-succession-text-secondary">tasks finished</p>
                </div>

                <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <ClockIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="font-semibold text-succession-text-primary">In Progress</h3>
                  </div>
                  <div className="text-3xl font-bold text-succession-text-primary">{inProgressTasks}</div>
                  <p className="text-sm text-succession-text-secondary">active task{inProgressTasks !== 1 ? 's' : ''}</p>
                </div>

                <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                      <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="font-semibold text-succession-text-primary">Messages</h3>
                  </div>
                  <div className="text-3xl font-bold text-succession-text-primary">{sprint?.unread_comment_count || 0}</div>
                  <p className="text-sm text-succession-text-secondary">unread messages</p>
                </div>
              </div>

              {program?.benefits && program.benefits.length > 0 && (
                <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-6">
                  <h3 className="font-semibold mb-4 text-succession-text-primary">What You'll Achieve</h3>
                  <ul className="space-y-2">
                    {program.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircleSolidIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-succession-text-primary">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {safeActiveTab === 'tasks' && (
            <div className="space-y-4">
              {tasks.length === 0 ? (
                <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-6 text-center text-succession-text-secondary">
                  No tasks yet. Tasks will appear here once the program starts.
                </div>
              ) : (
                <SprintKanbanBoard
                  items={tasks}
                  onItemUpdate={handleItemUpdate}
                  onItemClick={handleItemClick}
                  isCoachLed={isCoachLed}
                  isReadOnly={effectiveIsCoachView}
                  className="min-h-[400px]"
                  hideEmptyHint={false}
                  onCommentAdded={() => refetch()}
                />
              )}
            </div>
          )}

          {safeActiveTab === 'messages' && (
            <div className="space-y-4">
              {sprint?.id ? (
                <CoachesCornerComments
                  itemId={sprint.id}
                  itemType="Sprint"
                  maxHeight="calc(100vh - 500px)"
                  className="h-full flex flex-col border-0 rounded-none"
                  onCommentAdded={() => refetch()}
                />
              ) : (
                <div className="rounded-lg border border-succession-border bg-succession-bg-primary p-6">
                  <div className="text-center py-12 text-succession-text-secondary">
                    <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="mb-4">No sprint available for messaging</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {safeActiveTab === 'files' && (
            <EnrollmentFilesTab enrollment={enrollment} onRefetch={refetch} />
          )}
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          isOpen={isTaskModalOpen}
          onClose={handleTaskModalClose}
          task={selectedTask}
          onSave={handleTaskSave}
          onMarkCompleted={handleTaskMarkCompleted}
          onDelete={handleTaskDelete}
          onTaskUpdated={async () => {
            const result = await refetch();
            // Update selectedTask with fresh data including documents after refetch
            if (selectedTask && result.data?.sprintEnrollment?.sprint?.items) {
              const updatedTask = result.data.sprintEnrollment.sprint.items.find((item) => item.id === selectedTask.id);
              if (updatedTask) {
                setSelectedTask(updatedTask);
              }
            }
          }}
          isCoachView={effectiveIsCoachView}
          onReview={handleTaskReview}
          sprintProgramName={program?.title}
        />
      )}
    </div>
  );
}
